/**
 * Delivery URLs: S3 masters in DB; serve via ImageKit when configured.
 */
import { getImageKitEndpoint } from './imageKitConfig';

export { getImageKitEndpoint } from './imageKitConfig';

export function isImageKitDeliveryUrl(url, ikEndpoint = getImageKitEndpoint()) {
  if (!url || typeof url !== 'string') return false;
  if (ikEndpoint && url.startsWith(ikEndpoint)) return true;
  return url.includes('ik.imagekit.io');
}

export function extractMediaStoragePath(url) {
  if (!url || typeof url !== 'string') return null;

  const ik = getImageKitEndpoint();
  if (ik && url.startsWith(ik)) {
    try {
      const u = new URL(url);
      return decodeURIComponent(u.pathname.replace(/^\//, '').split('?')[0]);
    } catch {
      return null;
    }
  }

  if (!url.includes('amazonaws.com')) return null;

  try {
    const u = new URL(url);
    let objectPath = decodeURIComponent(u.pathname.replace(/^\//, ''));
    if (u.hostname.startsWith('s3.') || u.hostname.startsWith('s3-')) {
      const parts = objectPath.split('/');
      if (parts.length > 1) return parts.slice(1).join('/');
    }
    return objectPath;
  } catch {
    return null;
  }
}

export function isS3MediaUrl(url) {
  return Boolean(extractMediaStoragePath(url)) && url.includes('amazonaws.com');
}

function imageKitTransform(maxWidth) {
  return `w-${maxWidth},f-auto,q-auto,c-at_max`;
}

function proxyDeliverUrl(key, width, proxyBase) {
  const base = (proxyBase || '').replace(/\/$/, '');
  if (!base || !key) return null;
  const params = new URLSearchParams({ key, w: String(width) });
  return `${base}?${params.toString()}`;
}

export function deliveryImageUrl(url, maxWidth = 720, ikEndpoint = getImageKitEndpoint()) {
  if (!url || typeof url !== 'string') return url;
  if (!ikEndpoint) return url;

  const path = extractMediaStoragePath(url);
  if (!path) return url;

  if (isImageKitDeliveryUrl(url, ikEndpoint)) {
    try {
      const u = new URL(url);
      u.searchParams.set('tr', imageKitTransform(maxWidth));
      return u.toString();
    } catch {
      return url;
    }
  }

  return `${ikEndpoint}/${path}?tr=${imageKitTransform(maxWidth)}`;
}

export function buildDeliverySrcSet(url, widths, ikEndpoint = getImageKitEndpoint()) {
  if (!url || !Array.isArray(widths) || widths.length === 0) {
    return { srcSet: null, src: url };
  }

  const path = extractMediaStoragePath(url);
  if (!path || !ikEndpoint) {
    return { srcSet: null, src: url };
  }

  const sorted = [...new Set(widths)].sort((a, b) => a - b);
  const pairs = sorted.map((w) => ({
    w,
    u: `${ikEndpoint}/${path}?tr=${imageKitTransform(w)}`
  }));
  const srcSet = pairs.map((p) => `${p.u} ${p.w}w`).join(', ');
  return { srcSet, src: pairs[pairs.length - 1].u };
}

/** API proxy over S3 (webp resize) when ImageKit origin is not configured */
export function buildProxySrcSet(url, widths, proxyBase) {
  if (!url || !Array.isArray(widths) || widths.length === 0) {
    return { srcSet: null, src: url };
  }

  const path = extractMediaStoragePath(url);
  if (!path || !proxyBase) {
    return { srcSet: null, src: url };
  }

  const sorted = [...new Set(widths)].sort((a, b) => a - b);
  const pairs = sorted
    .map((w) => ({ w, u: proxyDeliverUrl(path, w, proxyBase) }))
    .filter((p) => p.u);
  if (pairs.length === 0) {
    return { srcSet: null, src: url };
  }
  const srcSet = pairs.map((p) => `${p.u} ${p.w}w`).join(', ');
  return { srcSet, src: pairs[pairs.length - 1].u };
}

export function proxyDeliveryImageUrl(url, maxWidth = 720, proxyBase) {
  const path = extractMediaStoragePath(url);
  if (!path || !proxyBase) return url;
  return proxyDeliverUrl(path, maxWidth, proxyBase) || url;
}

/** S3 or ImageKit URL — safe to call delete API */
export function isManagedMediaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return isS3MediaUrl(url) || isImageKitDeliveryUrl(url);
}

function imageKitVideoTransform(maxWidth) {
  const parts = [];
  if (maxWidth) parts.push(`w-${maxWidth}`);
  parts.push('q-70', 'f-auto');
  return parts.join(',');
}

function proxyVideoDeliverUrl(key, proxyBase) {
  const base = (proxyBase || '').replace(/\/$/, '');
  if (!base || !key) return null;
  return `${base}?${new URLSearchParams({ key }).toString()}`;
}

/**
 * Delivery URL for S3-backed video (API stream proxy with Range support).
 * ImageKit video is skipped — transforms hit account limits and HEVC masters need direct stream.
 * Local paths (/reels/…) are returned unchanged.
 */
export function buildVideoDeliveryUrl(
  url,
  {
    mediaVideoDeliveryBase = ''
  } = {}
) {
  if (!url || typeof url !== 'string') return url;

  const path = extractMediaStoragePath(url);
  if (!path) return url;

  if (mediaVideoDeliveryBase) {
    return proxyVideoDeliverUrl(path, mediaVideoDeliveryBase) || url;
  }

  return url;
}

/** @deprecated use buildVideoDeliveryUrl */
export function deliveryVideoUrl(url, ikEndpoint = getImageKitEndpoint()) {
  return buildVideoDeliveryUrl(url, { endpoint: ikEndpoint, deliveryMode: 'imagekit' });
}

export function canUseMediaDeliveryLayer(url) {
  return Boolean(extractMediaStoragePath(url));
}
